import React, { useEffect, useState, useCallback } from "react";
import Link from "next/link";
import classNames from "classnames";

import { signIn, createMagicLink, SignInResult, CreateMagicLinkResult } from "lib/api";
import { runAtLeast } from "lib/runAtLeast";
import useSession from "lib/useSession";
import useApiForm from "lib/useApiForm";
import Navbar from "components/Navbar";
import PageContent from "components/PageContent";
import SharedFooter from "components/SharedFooter";
import ApplicationLayout from "components/ApplicationLayout";
import SocialButton from "components/SocialButton";
import PageTransitions from "components/PageTransitions";
import StateMessage from "components/StateMessage";
import Input from "components/form/Input";
import Submit from "components/form/Submit";
import ArrowRight from "assets/icons/arrow-right.svg";
import ArrowLeft from "assets/icons/arrow-left.svg";
import GithubDarkIcon from "assets/icons/github-dark.svg";
import GoogleIcon from "assets/google-icon.svg";
import Logo from "assets/logo.svg";

import styles from "./styles.module.css";

import type { Page } from "types/page";

type FormAttributes = "email" | "password";
type ApiReturnType = SignInResult | CreateMagicLinkResult;

function openEmailWithFilterUrl(provider: string, sentFrom: string) {
  // TODO: add more providers?
  if (provider === "google") {
    return `https://mail.google.com/mail/u/0/#search/from%3A${encodeURIComponent(sentFrom)}+in%3Ainbox`;
  }
  return undefined;
}

const initialValues = {
  email: "",
  password: "",
};

let blurTimeout: NodeJS.Timeout | null = null;

const SignIn: Page = () => {
  const {
    values,
    errors,
    touched,
    sending,
    handleInvalid,
    handleChange,
    handleBlur,
    handleFocus,
    onSubmit,
    withServerData,
  } = useApiForm<FormAttributes, ApiReturnType>({ apiCall, onSuccess, onError, initialValues });

  const session = useSession();

  const [sentLinkResult, setSentLinkResult] = useState<
    | undefined
    | {
        message: string;
        email_sent_from: string;
        email_provider: "google" | null;
      }
  >(undefined);

  const [inputFocused, setInputFocused] = useState<boolean>(false);
  const [focusMode, setFocusMode] = useState<boolean>(false);

  function apiCall(values: Record<FormAttributes, string>): Promise<ApiReturnType> {
    if (values.password) {
      return runAtLeast(1300, signIn(values));
    }
    return runAtLeast(1300, createMagicLink({ email: values.email }));
  }

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    // prevent flickering during navigating between input fields
    blurTimeout = setTimeout(() => setInputFocused(false), 100);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e);
    if (blurTimeout) {
      clearTimeout(blurTimeout);
    }
    setInputFocused(true);
  };

  const resetForm = useCallback(() => {
    setSentLinkResult(undefined);
  }, [setSentLinkResult]);

  const handleEscOnPage = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        resetForm();
      }
    },
    [resetForm]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscOnPage);

    return () => document.removeEventListener("keydown", handleEscOnPage);
  }, [handleEscOnPage]);

  useEffect(() => {
    setFocusMode(!sentLinkResult && (inputFocused || Object.values(values).some((value) => !!value)));
  }, [values, inputFocused, sentLinkResult]);

  useEffect(() => {
    if (document !== undefined) {
      document.body.classList.toggle("focus-mode", focusMode);
    }
  }, [focusMode]);

  function onSuccess(result: ApiReturnType) {
    if ("user" in result) {
      onSignIn(result);
    } else if ("message" in result) {
      setSentLinkResult(result);
    }
  }

  function onError(error: unknown) {
    console.log(error);
    setSentLinkResult(undefined);
  }

  function onSignIn({ redirect_to }: { redirect_to: string }) {
    location.href = redirect_to;
    return;
  }

  if (session !== undefined && session.user !== undefined) {
    onSignIn(session);
    return <></>;
  }

  let stateMessage = "";
  if (sending && values.password) {
    stateMessage = "Logging in...";
  } else if (sending) {
    stateMessage = "Sending...";
  } else if (sentLinkResult) {
    stateMessage = "Magic link sent!";
  }

  return (
    <div className={styles.singleColWrapper}>
      <div className={styles.controlsWrapper}>
        <Logo className={styles.logo} />
        <h1 className={styles.header}>Log in to Raycast</h1>
        <p className={classNames(styles.notice, { [styles.invisible]: !sentLinkResult })}>
          We&rsquo;e sent a magic link to your&nbsp;
          {sentLinkResult && sentLinkResult.email_provider !== null ? (
            <a
              href={openEmailWithFilterUrl(sentLinkResult.email_provider, sentLinkResult.email_sent_from)}
              target="_blank"
              rel="noreferrer"
            >
              email inbox
            </a>
          ) : (
            "email inbox"
          )}
          . Click the link to login.
        </p>
        {sentLinkResult ? null : (
          <div className={styles.socialButtonsWrapper}>
            <SocialButton href="/auth/github">
              <GithubDarkIcon className={classNames("icon", styles.socialButtonIcon)} />
            </SocialButton>
            <SocialButton href="/auth/google">
              <GoogleIcon className={classNames("icon", styles.socialButtonIcon)} />
            </SocialButton>
            <p className={styles.orText}>or</p>
          </div>
        )}
        <form
          className={classNames(styles.form, {
            [styles.withoutPassword]: (sending && !values.password) || sentLinkResult,
          })}
          action=""
          onSubmit={onSubmit}
        >
          <Input
            type="email"
            name="email"
            placeholder="Email address"
            autoComplete="email"
            onInvalid={handleInvalid}
            error={errors.email || errors.base}
            onChange={(e) => {
              setSentLinkResult(undefined);
              handleChange(e);
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            required
            withServerData={withServerData}
          />
          <Input
            type="password"
            name="password"
            error={touched.password ? errors.password : ""}
            placeholder="Password (optional)"
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onInvalid={handleInvalid}
            withServerData={withServerData}
            autoComplete="current-password"
          />
          <div className={styles.formFooter}>
            {sending || sentLinkResult ? (
              <StateMessage state={sending ? "loading" : "success"} message={stateMessage} />
            ) : (
              <Submit
                disabled={sending || Object.values(errors).some((error) => !!error) || sentLinkResult}
                value={values.password ? "Login" : "Send Magic Link"}
              />
            )}
            {sentLinkResult ? (
              <button className={classNames(styles.otherOptionLink, styles.withoutBorder)} onClick={resetForm}>
                <ArrowLeft className={classNames("icon", styles.arrowLeft)} />
                &nbsp; Select other login option
              </button>
            ) : (
              <Link href="/users/sign_up">
                <a className={styles.otherOptionLink}>
                  Don&rsquo;t have an account? Sign up&nbsp;
                  <ArrowRight className={classNames("icon", styles.arrowRight)} />
                </a>
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

SignIn.getLayout = (page) => {
  return (
    <ApplicationLayout>
      <Navbar />
      <PageContent>
        <PageTransitions>{page}</PageTransitions>
      </PageContent>
      <SharedFooter />
    </ApplicationLayout>
  );
};

export default SignIn;
